import React, { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CircularProgress } from '@mui/material';

import EntityFormPage, { EntityForm } from '@entity/EntityForm/EntityForm';
import getGraphqlData from '@boilerplate/lib/getGraphqlData';
import { BaseFormUpdateProps } from '@boilerplate/types/entity';
import { addEntityToRelatedDataOptions } from '@boilerplate/lib/relatedDataToOptions';
import Notistack from '@/lib/notistack';
import CategoryEntity from '@/entities/category';
import { catchGraphQlError } from '@/lib/catchError';

  import LoggedHourEntity from '@/entities/loggedHour';
  import ProjectEntity from '@/entities/project';
  import TenantEntity from '@/entities/tenant';

function CategoryBaseUpdateForm({ formOnly = false, onClose, Entity = CategoryEntity, id: propId, ...props }: BaseFormUpdateProps<typeof CategoryEntity.model>) {
  const { id: paramId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const id = propId || paramId;

  const Component = formOnly ? EntityForm : EntityFormPage;

  const { loading, data } = CategoryEntity.model.useGet({
    variables: {
      id,
            withProjects: true,
            withParentCategory: true,
            withTenant: true,
    },
  });

  const [updateMutation, { loading: updateLoading }] = CategoryEntity.model.useUpdate();

  const relationsOptionsQuery = CategoryEntity.model.useRelationsOptions({
    variables: {
          withProjects: true,
          withParentCategories: true,
          withTenants: true,
    }
  });

  const relationsOptions = useMemo(
    () =>
      addEntityToRelatedDataOptions(relationsOptionsQuery, [
              ProjectEntity,
              TenantEntity,
      ]),
    [relationsOptionsQuery]
  );

  const handleSubmit = (newData) => {
    newData.variables = getGraphqlData(Entity, newData.variables, 'update');

    updateMutation(newData)
      .then((result) => {
        Notistack.toast(t('crud:updatedItem', { item: t('entities:category') }), { variant: 'success' });

        if (!formOnly) {
          navigate(-1);
        }

        return onClose?.(result.data?.updateCategory);
      })
      .catch(catchGraphQlError);
  };

  if (loading || relationsOptionsQuery.loading) {
    return <CircularProgress />;
  }

  return (
    <Component
      {...props}
      type="update"
      Entity={Entity}
      loading={updateLoading}
      handleSubmit={handleSubmit}
      relationsOptions={relationsOptions}
      data={data.category}
    />
  );
}

export default CategoryBaseUpdateForm;
